import React, { useState } from "react";
import { Card, Flex, Text, Button, Badge } from "@radix-ui/themes";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AuthAtreemoDialog } from "./AuthAtreemoDialog";
import { PermissionAlertDialog } from "components";
import { useValidateAtreemo, useGetAtreemoStatus } from "hooks"; 
import { set } from "date-fns";

export function BrowseAtreemoCard({ userProfile }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [host, setHost] = useState("");
  const [isUsernameValid, setIsUsernameValid] = useState(true);
  const [isPasswordValid, setIsPasswordValid] = useState(true);
  const [isHostValid, setIsHostValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [progressValue, setProgressValue] = useState(0);

  const validateAtreemo = useValidateAtreemo();
  const { data: atreemoStatusData, isLoading: isStatusLoading } = useGetAtreemoStatus(userProfile.organisation_id);
  const [openDialog, setOpenDialog] = useState(false);
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);

  const isAtreemoActive = () => {
    if (isStatusLoading) return false;
    return atreemoStatusData?.data?.is_active === true;
  };

  const handleOpenChange = (isOpen) => {
    setOpenDialog(isOpen);
    if (!isOpen) {
      handleCancelAuthDialog();
    }
  };

  const handleConnectClick = () => {
    // Check if user is owner
    const isOwner = userProfile.user_role === "Owner";
    
    if (isOwner) {
      handleOpenChange(true);
    } else {
      setShowPermissionAlert(true);
    }
  };

  const handleCancelAuthDialog = () => {
    setUsername("");
    setPassword("");
    setHost("");
    setIsUsernameValid(true);
    setIsPasswordValid(true);
    setIsHostValid(true);
  };

  const handleUsernameChange = (event) => {
    setUsername(event.target.value);
    setIsUsernameValid(true);
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    setIsPasswordValid(true);
  };

  const handleHostChange = (event) => {
    setHost(event.target.value);
    setIsHostValid(true);
  };

  // Helper: return true only if value is a valid URL and uses https:
  const isHostHttps = (value) => {
    if (!value) return false;
    try {
      const url = new URL(value.trim());
      return url.protocol === "https:";
    } catch {
      return false;
    }
  };

  const handleSave = async () => {
    let isValid = true;

    // Check both fields for emptiness
    if (!username) {
      setIsUsernameValid(false);
      isValid = false;
    }

    if (!password) {
      setIsPasswordValid(false);
      isValid = false;
    }

    if (!host) {
      setIsHostValid(false);
      isValid = false;
    } else if (!isHostHttps(host)) {
      // host is provided but not valid **https** URL
      setIsHostValid(false);
      isValid = false;
    }

    // Only proceed if both fields are filled
    if (!isValid) {
      return;
    }

    setIsLoading(true);
    setProgressValue(10);

    try {
      const result = await validateAtreemo.mutateAsync({
        username: username,
        password: password,
        host: host,
        organisation_id: userProfile.organisation_id,
      });

      setProgressValue(50);

      // If validation was successful, proceed to the next step
      if (result?.data?.status === "success") {
        // Navigate to setup page
        navigate("/atreemo-setup", {
          state: {
            username,
            password,
            host,
            orgId: userProfile.organisation_id,
            access_token: result.data.access_token,
            access_token_expires_at: result.data.access_token_expires_at,
            available_brands: result.data.available_brands,
            available_locations: result.data.available_locations
          },
        });
      } else {
          const invalid = result?.data?.invalid;

          if (invalid === "credentials") {
            // backend indicates credentials invalid -> both username and password
            setIsUsernameValid(false);
            setIsPasswordValid(false);
            setIsHostValid(true);
          } else if (invalid === "host") {
            // backend indicates host/schema/connection problem
            setIsUsernameValid(true);
            setIsPasswordValid(true);
            setIsHostValid(false);
          } else {
            // unknown -> mark all invalid
            setIsUsernameValid(false);
            setIsPasswordValid(false);
            setIsHostValid(false);
          }
        }
    } catch (error) {
      console.error("Validation error:", error);

      // Check which field is invalid based on the error response
      const invalidField = error.response?.data?.invalid;

      if (invalidField === "credentials") {
        // can't distinguish username vs password -> mark both invalid
        setIsUsernameValid(false);
        setIsPasswordValid(false);
        setIsHostValid(true);
      } else if (invalidField === "host") {
        // host/schema/connection error
        setIsUsernameValid(true);
        setIsPasswordValid(true);
        setIsHostValid(false);
      } else {
        // unknown / network error -> mark all invalid for safety
        setIsUsernameValid(false);
        setIsPasswordValid(false);
        setIsHostValid(false);
      }
    } finally {
      setIsLoading(false);
      setProgressValue(0);
    }
  };

  return (
    <>
      <Card
        style={{
          display: "flex",
          minWidth: "455px",
          padding: "var(--space-4)",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Flex align="center" gap="4">
          <Flex
            style={{
              borderRadius: "var(--space-1)",
              background: "#FFFFFF",
              border: "1px solid var(--gray-a6)",
            }}
            height="40px"
            width="40px"
            p="2"
            justify="center"
            align="center"
          >
            <img src={require("../../../assets/images/atreemo.jpg")} alt="atreemo" style={{ width: "24px" }} />
          </Flex>
          <Flex direction="column" justify="center" align="start">
            <Text size="4" weight="medium">
              Atreemo
            </Text>
            <Text style={{ color: "var(--gray-a11)" }} size="1">
              CRM & Email marketing
            </Text>
          </Flex>
        </Flex>

        <Flex align="center">
          {isAtreemoActive() ? (
            <Badge size="1" variant="soft" color="green">
              Connected
            </Badge>
          ) : (
            <Button onClick={handleConnectClick} size="2" variant="soft" color="gray">
              Connect
            </Button>
          )}
        </Flex>
      </Card>

      <AuthAtreemoDialog
        isOpen={openDialog}
        onOpenChange={handleOpenChange}
        onCancel={handleCancelAuthDialog}
        onSave={handleSave}
        handleUsernameChange={handleUsernameChange}
        handlePasswordChange={handlePasswordChange}
        handleHostChange={handleHostChange}
        isUsernameValid={isUsernameValid}
        isPasswordValid={isPasswordValid}
        isHostValid={isHostValid}
        isLoading={isLoading}
        progressValue={progressValue}
      />

      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
    </>
  );
}
